import { useEffect, useRef } from 'react';
import { useRouteError, useNavigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Dialog, DialogContent, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import { config } from '@converge/config';
import {
  ConvergeButton,
  CustomPaletteColor,
  ErrorPageLayout,
  useConvergeNavigation,
} from '@converge/ui';
import './error-boundary.page.scss';

const StyledPreBox = styled(Box)(() => ({
  border: '1px solid #C4C4C4',
  borderRadius: '4px',
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  padding: 10,
  width: '100%',
  maxWidth: '100%',
  overflow: 'auto',
}));

const StyledTypography = styled(Typography)(() => ({
  fontSize: '12px',
  margin: '10px 0 30px 0',
}));

const StyledButtonContainer = styled(Box)(() => ({
  display: 'flex',
  columnGap: '15px',
  '.MuiButton-root': {
    fontSize: '14px',
    height: '30px',
  },
}));

export const ErrorBoundaryPage = () => {
  const preFormattedRef = useRef<HTMLPreElement>(null);
  const error = useRouteError() as Error;
  const navigate = useNavigate();
  const location = useLocation();
  const convergeNavigate = useConvergeNavigation();
  const handleHomeClickEvent = () => {
    convergeNavigate.home();
  };

  useEffect(() => {
    Sentry.captureException(error, {
      extra: { type: 'error-bound' },
      tags: { route: location.pathname },
    });
  }, [error, location.pathname]);

  return (
    <>
      <AppBar
        position="static"
        elevation={1}
        className="error-boundary-app-bar"
      >
        <Toolbar>
          <Box width={'25%'}>
            <img
              src={config.logo}
              className="AppBar-logo"
              alt="logo"
              onClick={handleHomeClickEvent}
            />
          </Box>
        </Toolbar>
      </AppBar>
      <Dialog open fullWidth maxWidth={false}>
        <DialogContent className="error-boundary-dialog-content-container">
          <ErrorPageLayout
            message="Error"
            messageId="error"
            color={CustomPaletteColor.Secondary}
          />
          <StyledTypography paragraph={true} color="black" textAlign={'center'}>
            There was an error rendering this page. The error was reported to
            the development team.
          </StyledTypography>
          <Box width={'100%'}>
            <Typography component="span" color={CustomPaletteColor.LightGrey}>
              Details
            </Typography>
          </Box>
          <StyledPreBox>
            <pre
              className="error-boundary-reFormattedRef"
              ref={preFormattedRef}
              onClick={() => {
                if (!preFormattedRef.current) {
                  return;
                }

                const range = document.createRange();
                range.selectNodeContents(preFormattedRef.current);

                const sel = window.getSelection();
                if (sel) {
                  sel.removeAllRanges();
                  sel.addRange(range);
                }
              }}
            >
              {`${error.message ?? ''}\n`}
              {error?.stack ?? ''}
            </pre>
          </StyledPreBox>
          <StyledButtonContainer padding={2}>
            <ConvergeButton
              label="Back"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
            />
            <ConvergeButton
              label="Go Home"
              variant="contained"
              color="primary"
              onClick={handleHomeClickEvent}
            />
            <ConvergeButton
              label="Refresh"
              variant="contained"
              color="primary"
              onClick={() => navigate(0)}
            />
          </StyledButtonContainer>
        </DialogContent>
      </Dialog>
    </>
  );
};
